/**
 * Words for exam prep (#440, #362), English and Hebrew. Sizes (topics, sessions, minutes) are plan sizes, never a measure of
 * what the learner knows. Every tier and outcome carries a symbol AND words; none is colour alone.
 */

import type { ExamPrepItem, ExamPrepPlanResponse, ExamPrepSessionSummaryResponse } from '@davinci/contracts';
import { useLocale } from '../../i18n/locale.js';

export type Tier = ExamPrepPlanResponse['topics'][number]['tier'];
export type Outcome = ExamPrepSessionSummaryResponse['items'][number]['outcome'];
export type UnparseableReason = Exclude<NonNullable<ExamPrepItem['attempts'][number]['unparseableReason']>, 'null'>;
export type IncorrectReason = NonNullable<ExamPrepItem['attempts'][number]['incorrectReason']>;
export type Unavailable = 'assessment_passed' | 'plan_has_no_problems' | 'nothing_due_yet';

const en = {
    navLabel: 'Exam preparation',
    heading: 'Exam preparation',
    loading: 'Loading…',
    noCourse: 'Choose a course above to prepare for its assessments.',
    noAssessments: 'No upcoming assessment for this course yet.',
    addAssessment: 'Add a quiz or exam',
    pickAssessment: 'Assessment',
    makePlan: 'Make my plan',
    eyebrow: (course: string, kind: string, date: string) => `${course} · ${kind}, ${date}`,
    topicsToWork: (topics: number) => `${String(topics)} ${topics === 1 ? 'topic' : 'topics'} to work on`,
    aboutSessions: (sessions: number, minutes: number) => `About ${String(sessions)} ${sessions === 1 ? 'session' : 'sessions'} of ${String(minutes)} min`,
    sessionsLabel: 'Sessions',
    sessionCount: (n: number) => (n === 1 ? '1 session' : `${String(n)} sessions`),
    sessionDone: (n: number) => `Session ${String(n)}: done`,
    sessionToDo: (n: number) => `Session ${String(n)}: to do`,
    topicsHeading: 'Topics, in the order you will meet them',
    moveUp: (name: string) => `Move ${name} up`,
    moveDown: (name: string) => `Move ${name} down`,
    topicFallback: 'A topic of this course',
    from: 'From',
    fromSemester: 'From your semester',
    tier: {
      sure_then_wrong: 'a ✗ you were sure of',
      wrong: '✗ wrong',
      fixed_with_help: '◐ fixed only with help',
      not_tested_in_app: '○ not tried in the app yet',
    } satisfies Record<Tier, string>,
    withoutHeading: 'DaVinci cannot make checkable problems for these. Go back to the sheet and its worked solution.',
    noCheckable: 'DaVinci has no problems it can check for this assessment yet, and it will not invent any.',
    passed: 'This assessment has passed.',
    start: 'Start a session',
    continue: 'Continue the session',
    unavailable: {
      assessment_passed: 'This assessment has passed.',
      plan_has_no_problems: 'There are no problems DaVinci can check for this plan.',
      nothing_due_yet: 'Nothing is due yet. The next session opens when solved topics come back.',
    } satisfies Record<Unavailable, string>,
    failed: 'That did not work. Try again.',
    // The item screen.
    sessionHeading: 'Exam prep session',
    steps: 'Problems',
    stepDone: (n: number) => `Problem ${String(n)}: done`,
    stepNow: (n: number) => `Problem ${String(n)}: this one`,
    stepNext: (n: number) => `Problem ${String(n)}: still to come`,
    difficulty: 'Mixed practice feels harder than doing one kind in a row. That is the point: it is what holds up in the exam.',
    dismiss: 'Got it',
    reasonBack: 'Back again: you solved this alone 3+ days ago.',
    reasonNotYet: 'Back again: this one was not yet last time.',
    witness: (domain: string) => `Find values in ${domain} that make this true:`,
    counterexample: (domain: string) => `This claim is false. Find a counterexample in ${domain}:`,
    domain: { N0: 'ℕ (from 0)', N1: 'ℕ (from 1)', Z: 'ℤ', Q: 'ℚ' },
    answerLabel: 'Your answer',
    previewLabel: 'How DaVinci reads it',
    previewNone: 'Not readable yet. Write it like x = 3, y = -1',
    submit: 'Submit',
    unparseableTitle: 'DaVinci could not read that answer, so nothing was marked.',
    unparseable: {
      empty: 'The answer is empty.',
      not_an_assignment: 'Write each value as name = value.',
      two_equals: 'Each part needs exactly one = sign.',
      bad_variable_name: 'A name on the left is not a valid variable.',
      unknown_variable: 'A name on the left is not one of this problem’s variables.',
      variable_assigned_twice: 'A variable appears twice.',
      variable_unassigned: 'Every variable needs a value.',
      value_not_exact: 'Write exact values, such as 3 or -1/2.',
    } satisfies Record<UnparseableReason, string>,
    unparseableOther: 'Check the format and try again.',
    correct: '✓ Correct',
    correctWithHelp: '✓ Correct, with a hint',
    incorrect: '✗ Not this one',
    incorrectWhy: {
      condition_false: 'A condition is false for these values.',
      condition_undefined: 'A condition is undefined for these values.',
      not_in_domain: 'A value is not in the allowed set.',
      hypothesis_false: 'These values break the given conditions. A counterexample must satisfy them.',
      conclusions_hold: 'The claim still holds for these values. A counterexample must make it fail.',
    } satisfies Record<Exclude<IncorrectReason, 'null'>, string>,
    yourAnswer: 'Your answer',
    hintButton: 'Show a hint',
    hintCost: 'Counts as with help.',
    hint: (bound: string) => `Try values within ${bound}.`,
    unknownButton: 'I don’t know yet',
    unknownCost: 'Shows the answer; this one comes back in a later session.',
    revealed: 'The answer was',
    revealedAfter: 'This one comes back in a later session.',
    pointerTitle: 'DaVinci can’t check a proof here.',
    pointerGoBefore: 'Go back to ',
    pointerGoAfter: ' and the worked solution.',
    pointerSheet: 'the sheet',
    next: 'Next',
    finishing: 'Finishing…',
    done: 'Done.',
    outcome: {
      on_your_own: '✓ on your own',
      with_help: '◐ with a hint',
      not_yet: '○ not yet',
      proof_pointer: '→ a proof: back to the sheet',
    } satisfies Record<Outcome, string>,
    backToPlan: 'Back to the plan',
    noSession: 'This session is not available.',
};

type PrepMessages = typeof en;

const he: PrepMessages = {
    navLabel: 'הכנה למבחן',
    heading: 'הכנה למבחן',
    loading: 'טוען…',
    noCourse: 'בחרו קורס למעלה כדי להתכונן להערכות שלו.',
    noAssessments: 'עדיין אין הערכה קרובה בקורס הזה.',
    addAssessment: 'הוספת בוחן או מבחן',
    pickAssessment: 'הערכה',
    makePlan: 'בנו לי תוכנית',
    eyebrow: (course: string, kind: string, date: string) => `${course} · ${kind}, ${date}`,
    topicsToWork: (topics: number) => (topics === 1 ? 'נושא אחד לעבוד עליו' : `${String(topics)} נושאים לעבוד עליהם`),
    aboutSessions: (sessions: number, minutes: number) => `בערך ${String(sessions)} מפגשים של ${String(minutes)} דקות`,
    sessionsLabel: 'מפגשים',
    sessionCount: (n: number) => (n === 1 ? 'מפגש אחד' : `${String(n)} מפגשים`),
    sessionDone: (n: number) => `מפגש ${String(n)}: הושלם`,
    sessionToDo: (n: number) => `מפגש ${String(n)}: עוד לא`,
    topicsHeading: 'הנושאים, בסדר שבו תפגשו אותם',
    moveUp: (name: string) => `הזיזו את ${name} למעלה`,
    moveDown: (name: string) => `הזיזו את ${name} למטה`,
    topicFallback: 'נושא בקורס',
    from: 'מתוך',
    fromSemester: 'מהסמסטר שלכם',
    tier: {
      sure_then_wrong: 'טעות ✗ שהייתם בטוחים בה',
      wrong: '✗ שגוי',
      fixed_with_help: '◐ תוקן רק בעזרה',
      not_tested_in_app: '○ עוד לא נוסה באפליקציה',
    },
    withoutHeading: 'DaVinci לא יכול ליצור עבורם תרגילים הניתנים לבדיקה. חזרו לגיליון ולפתרון המלא.',
    noCheckable: 'עדיין אין ל-DaVinci תרגילים שהוא יכול לבדוק להערכה הזו, והוא לא ממציא.',
    passed: 'ההערכה הזו כבר עברה.',
    start: 'התחילו מפגש',
    continue: 'המשיכו במפגש',
    unavailable: {
      assessment_passed: 'ההערכה הזו כבר עברה.',
      plan_has_no_problems: 'אין תרגילים ש-DaVinci יכול לבדוק בתוכנית הזו.',
      nothing_due_yet: 'אין כרגע מה לתרגל. המפגש הבא נפתח כשנושאים שפתרתם יחזרו.',
    },
    failed: 'זה לא הצליח. נסו שוב.',
    sessionHeading: 'מפגש הכנה למבחן',
    steps: 'תרגילים',
    stepDone: (n: number) => `תרגיל ${String(n)}: הושלם`,
    stepNow: (n: number) => `תרגיל ${String(n)}: זה שעכשיו`,
    stepNext: (n: number) => `תרגיל ${String(n)}: עוד יבוא`,
    difficulty: 'תרגול מעורב מרגיש קשה יותר מתרגול של סוג אחד ברצף. זו הנקודה: כך הידע מחזיק במבחן.',
    dismiss: 'הבנתי',
    reasonBack: 'חוזר: פתרתם את זה לבד לפני 3 ימים ויותר.',
    reasonNotYet: 'חוזר: בפעם הקודמת זה היה עוד לא.',
    witness: (domain: string) => `מצאו ערכים ב-${domain} שעבורם מתקיים:`,
    counterexample: (domain: string) => `הטענה הזו שגויה. מצאו דוגמה נגדית ב-${domain}:`,
    domain: { N0: 'ℕ (מ-0)', N1: 'ℕ (מ-1)', Z: 'ℤ', Q: 'ℚ' },
    answerLabel: 'התשובה שלכם',
    previewLabel: 'כך DaVinci קורא את זה',
    previewNone: 'עוד לא ניתן לקריאה. כתבו כך: x = 3, y = -1',
    submit: 'שליחה',
    unparseableTitle: 'DaVinci לא הצליח לקרוא את התשובה, ולכן לא סומן כלום.',
    unparseable: {
      empty: 'התשובה ריקה.',
      not_an_assignment: 'כתבו כל ערך כך: שם = ערך.',
      two_equals: 'בכל חלק צריך סימן = אחד בדיוק.',
      bad_variable_name: 'שם בצד שמאל אינו משתנה תקין.',
      unknown_variable: 'שם בצד שמאל אינו אחד ממשתני התרגיל.',
      variable_assigned_twice: 'משתנה מופיע פעמיים.',
      variable_unassigned: 'לכל משתנה צריך ערך.',
      value_not_exact: 'כתבו ערכים מדויקים, כמו 3 או -1/2.',
    },
    unparseableOther: 'בדקו את הפורמט ונסו שוב.',
    correct: '✓ נכון',
    correctWithHelp: '✓ נכון, עם רמז',
    incorrect: '✗ לא זה',
    incorrectWhy: {
      condition_false: 'תנאי אחד לא מתקיים עבור הערכים האלה.',
      condition_undefined: 'תנאי אחד לא מוגדר עבור הערכים האלה.',
      not_in_domain: 'ערך אחד אינו בקבוצה המותרת.',
      hypothesis_false: 'הערכים האלה מפרים את התנאים הנתונים. דוגמה נגדית חייבת לקיים אותם.',
      conclusions_hold: 'הטענה עדיין מתקיימת עבור הערכים האלה. דוגמה נגדית חייבת לסתור אותה.',
    },
    yourAnswer: 'התשובה שלכם',
    hintButton: 'הצגת רמז',
    hintCost: 'ייחשב כ"עם עזרה".',
    hint: (bound: string) => `נסו ערכים בתוך ${bound}.`,
    unknownButton: 'עוד לא יודע',
    unknownCost: 'מציג את התשובה; התרגיל הזה יחזור במפגש מאוחר יותר.',
    revealed: 'התשובה הייתה',
    revealedAfter: 'התרגיל הזה יחזור במפגש מאוחר יותר.',
    pointerTitle: 'DaVinci לא יכול לבדוק הוכחה כאן.',
    pointerGoBefore: 'חזרו אל ',
    pointerGoAfter: ' ואל הפתרון המלא.',
    pointerSheet: 'הגיליון',
    next: 'הבא',
    finishing: 'מסיים…',
    done: 'סיימנו.',
    outcome: {
      on_your_own: '✓ לבד',
      with_help: '◐ עם רמז',
      not_yet: '○ עוד לא',
      proof_pointer: '→ הוכחה: חזרה לגיליון',
    },
    backToPlan: 'חזרה לתוכנית',
    noSession: 'המפגש הזה לא זמין.',
};

export function usePrep(): PrepMessages {
  return useLocale().locale === 'he' ? he : en;
}
